import { toolName } from '@/app/technical';
import { scopeWords } from '@/ui/words';
import { useState } from 'react';
import { failureOf } from '@/api/errors';
import {
  useAdmitHostedClient,
  useWithdrawHostedClient,
  useAdmitTool,
  useAssignments,
  useConnections,
  useEndAssignment,
  useGrantAssignment,
  useKillSwitch,
  useRestoreAgent,
  useResumeDepartment,
  useRevokeAgent,
  useSetAutonomy,
  useSuspendDepartment,
  useWithdrawTool,
} from '@/api/hooks';
import type { AutonomyDimension, WireAgent, WireDepartmentDetail, WireDimension, WireHostedClient, WireTool } from '@/api/wire';
import { useDepartmentLabel } from '@/app/departments';
import { Dialog } from '@/ui/Dialog';
import { Button, Field, TextInput } from '@/ui/primitives';
import { OptionRow, TokenPanel } from '@/ui/records';
import { Meter, LEVEL_SHORT, type Level } from '@/ui/meter';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { Textarea } from '@/ui/fields';
import { FillForMe } from '@/ui/fill';
import { dateIn, endChoices, endsAt, grantTermDays, withinTerm } from './terms';

/**
 * The oversight flows (docs/frontend/handoff data/flows.js, ADR-0073). Each
 * is one dialog over one control behind the admin door. A control that
 * spends a token ends on the token panel, minted from the superadmin's own
 * session; a control that only takes something away — ending an assignment,
 * lowering a level — spends nothing and says so. Every one is reversible
 * from the same screen, and the danger ones say "Reversible" in the footer.
 */

const DANGER_NOTE = 'Reversible';

export function RevokeAgent({ agent, onClose }: { agent: WireAgent; onClose: () => void }) {
  const revoke = useRevokeAgent();
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState('');
  const [token, setToken] = useState('');
  const target = `agent:${agent.handle}`;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Kill switch · critical"
      title="Pause this agent?"
      danger
      note={DANGER_NOTE}
      secondary={{ label: step === 1 ? 'Cancel' : 'Back', onClick: () => (step === 1 ? onClose() : setStep(1)) }}
      primary={
        step === 1
          ? { label: 'Next', disabled: reason.trim().length < 8, onClick: () => setStep(2) }
          : {
              label: 'Mint the token and pause',
              disabled: token === '' || revoke.isPending,
              onClick: () =>
                revoke.mutate(
                  { agentId: agent.id, reason: reason.trim(), supervisionToken: token },
                  { onSuccess: () => { toast({ text: `${agent.givenName} lost access immediately.`, tone: 'danger' }); onClose(); } },
                ),
            }
      }
    >
      {step === 1 ? (
        <>
          <p className="m-0 text-body">
            It loses every scope immediately and stops mid-task. Its history and everything it built stay exactly where they are.
          </p>
          <Field label="Why" hint="Read by whoever starts it again, and by the audit log.">
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A sentence a person can act on" aria-label="Reason" />
          </Field>
        </>
      ) : (
        <>
          <p className="m-0 text-body">Bound to this one agent. A token minted here cannot engage the kill switch or stop anyone else.</p>
          <TokenPanel operation="agent.revoke" target={target} token={token} onToken={setToken} canMint />
          {revoke.isError && <Refusal failure={failureOf(revoke.error)} compact />}
        </>
      )}
    </Dialog>
  );
}

export function RestoreAgent({ agent, onClose }: { agent: WireAgent; onClose: () => void }) {
  const restore = useRestoreAgent();
  const toast = useToast();
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Restore access"
      title="Start this agent again?"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Start it',
        disabled: token === '' || restore.isPending,
        onClick: () =>
          restore.mutate(
            { agentId: agent.id, supervisionToken: token },
            { onSuccess: () => { toast({ text: `${agent.givenName} is back on.` }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">It gets back exactly the scopes it had before, nothing more. Its assignments were never touched.</p>
      {agent.revocationReason && (
        <p className="m-0 text-dense text-secondary">Paused {agent.revokedBy ? `by ${agent.revokedBy}` : ''}: “{agent.revocationReason}”</p>
      )}
      <TokenPanel operation="agent.restore" target={`agent:${agent.handle}`} token={token} onToken={setToken} canMint />
      {restore.isError && <Refusal failure={failureOf(restore.error)} compact />}
    </Dialog>
  );
}

export function StopDepartment({ department, onClose }: { department: string; onClose: () => void }) {
  const departmentLabel = useDepartmentLabel();
  const suspend = useSuspendDepartment();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Kill switch"
      title="Stop this department's agents?"
      danger
      note={DANGER_NOTE}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and stop them',
        disabled: token === '' || reason.trim().length < 8 || suspend.isPending,
        onClick: () =>
          suspend.mutate(
            { department, reason: reason.trim(), supervisionToken: token },
            { onSuccess: () => { toast({ text: `${departmentLabel(department)}'s agents are stopped.`, tone: 'danger' }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">Every agent in this department stops mid-flight. Other departments keep running. Nothing is deleted.</p>
      <Field label="Why">
        <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A sentence a person can act on" aria-label="Reason" />
      </Field>
      <TokenPanel operation="department.suspend" target={`department:${department}`} token={token} onToken={setToken} canMint />
      {suspend.isError && <Refusal failure={failureOf(suspend.error)} compact />}
    </Dialog>
  );
}

export function ResumeDepartment({ department, onClose }: { department: string; onClose: () => void }) {
  const departmentLabel = useDepartmentLabel();
  const resume = useResumeDepartment();
  const toast = useToast();
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Kill switch"
      title="Start this department's agents again?"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Start them',
        disabled: token === '' || resume.isPending,
        onClick: () =>
          resume.mutate(
            { department, supervisionToken: token },
            { onSuccess: () => { toast({ text: `${departmentLabel(department)}'s agents are running again.` }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">Each agent gets back exactly the scopes it had. Nothing was lost while they were stopped.</p>
      <TokenPanel operation="department.resume" target={`department:${department}`} token={token} onToken={setToken} canMint />
      {resume.isError && <Refusal failure={failureOf(resume.error)} compact />}
    </Dialog>
  );
}

export function KillSwitch({ engaged, onClose }: { engaged: boolean; onClose: () => void }) {
  const kill = useKillSwitch();
  const toast = useToast();
  const [token, setToken] = useState('');
  const stopping = !engaged;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={stopping ? 'Kill switch · critical' : 'Kill switch'}
      title={stopping ? 'Stop every agent, everywhere?' : 'Start every agent again?'}
      danger={stopping}
      note={stopping ? DANGER_NOTE : undefined}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: stopping ? 'Mint the token and stop' : 'Start them',
        disabled: token === '' || kill.isPending,
        onClick: () =>
          kill.mutate(
            { engaged: stopping, supervisionToken: token },
            {
              onSuccess: () => {
                toast(stopping ? { text: 'Every agent is stopped. Approvals and data are untouched.', tone: 'danger' } : { text: 'Agents are running again.' });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        {stopping
          ? 'Every agent in every department stops mid-flight. Nothing in the database changes, no approval is lost, and you can start them again whenever you want.'
          : 'Each agent gets back exactly the scopes it had. Nothing was lost while they were stopped.'}
      </p>
      <p className="m-0 text-dense text-secondary">
        Critical operations consume a single-use token, minted for this operation and bound to this target. It cannot be granted in advance, reused, or spent on anything else.
      </p>
      <TokenPanel operation="killswitch.change" target="killswitch:global" token={token} onToken={setToken} canMint />
      {kill.isError && <Refusal failure={failureOf(kill.error)} compact />}
    </Dialog>
  );
}

const DIMENSION_LABEL: Record<AutonomyDimension, string> = {
  change: 'Changes it ships',
  communication: 'Mail it sends',
  promotion: 'Strangers it promotes',
};

function needsSentence(d: WireDimension): string {
  if (!d.next) return 'Already at the highest level.';
  const short = LEVEL_SHORT[d.next as Level];
  if (d.canRaise) return `The evidence is there. ${short} can be granted — a superadmin still has to grant it.`;
  const parts = Object.entries(d.needed).map(([k, v]) => `${k} ${v}`);
  const have = Object.entries(d.counted).map(([k, v]) => `${k} ${typeof v === 'number' && v < 1 && v > 0 ? v.toFixed(2) : v}`);
  return `Not yet. ${short} needs ${parts.join(', ')}; counted so far: ${have.join(', ')}.`;
}

export { needsSentence, DIMENSION_LABEL };

export function SetAutonomy({ department, onClose }: { department: WireDepartmentDetail; onClose: () => void }) {
  const set = useSetAutonomy();
  const toast = useToast();
  const [dimension, setDimension] = useState<AutonomyDimension>('change');
  const current = department.dimensions.find((d) => d.dimension === dimension)!;
  const [level, setLevel] = useState<Level>(current.level);
  const [evidence, setEvidence] = useState('');
  const [token, setToken] = useState('');
  const order: Level[] = ['open_loop', 'mixed', 'closed_loop'];
  const raising = order.indexOf(level) > order.indexOf(current.level);
  const same = level === current.level;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Earned, not granted"
      title="Move the autonomy level"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: raising ? 'Mint the token and raise it' : 'Lower it',
        disabled: same || set.isPending || (raising && (token === '' || evidence.trim().length < 12 || !current.canRaise)),
        onClick: () =>
          set.mutate(
            { department: department.name, level, dimension, ...(raising ? { evidence: evidence.trim(), supervisionToken: token } : {}) },
            {
              onSuccess: () => {
                toast({ text: `${department.displayName} is at ${LEVEL_SHORT[level].toLowerCase()} for ${DIMENSION_LABEL[dimension].toLowerCase()}.` });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        Autonomy is earned per department as its own evidence proves it holds. Raising a level needs the counted evidence and a token; lowering one needs neither — someone who wants more oversight should never have to ask twice.
      </p>
      <OptionRow<AutonomyDimension>
        name="Dimension"
        value={dimension}
        onChoose={(d) => {
          setDimension(d);
          setLevel(department.dimensions.find((x) => x.dimension === d)!.level);
        }}
        options={department.dimensions.map((d) => ({ value: d.dimension, label: DIMENSION_LABEL[d.dimension], hint: `${LEVEL_SHORT[d.level as Level]} now` }))}
      />
      <Meter level={level} onSet={setLevel} />
      <p className={`m-0 text-dense ${raising && !current.canRaise ? 'text-danger' : 'text-meta'}`} data-testid="evidence-line">
        {raising
          ? needsSentence(current)
          : same
            ? `${department.displayName} is already there.`
            : 'Lowering a level needs no evidence and no argument.'}
      </p>
      {raising && (
        <>
          <Field label="Your evidence, in your words" hint="Recorded on the grant; the next person reads it.">
            <Textarea
              className="min-h-[88px]"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              aria-label="Evidence"
            />
          </Field>
          <TokenPanel operation="autonomy.change" target={`autonomy:${department.name}:${dimension}`} token={token} onToken={setToken} canMint />
        </>
      )}
      {set.isError && <Refusal failure={failureOf(set.error)} compact />}
    </Dialog>
  );
}

export function GrantAssignment({ agent, onClose, presetTools, presetTitle }: { agent: WireAgent; onClose: () => void; presetTools?: string[]; presetTitle?: string }) {
  const grant = useGrantAssignment();
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState(presetTitle ?? '');
  const [scopes, setScopes] = useState('');
  const [tools, setTools] = useState(presetTools?.join(', ') ?? '');
  const [until, setUntil] = useState(() => dateIn(30));
  const [token, setToken] = useState('');
  const list = (s: string) => s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
  // How long these tools may be granted for, said before the token is minted
  // (ADR-0085). Unknown while the terms load or if they cannot be read: then
  // core-api's refusal is the word, and it brings the person back here.
  const terms = useConnections(true);
  const limit = terms.data ? grantTermDays(list(tools), terms.data) : null;
  const [refusedEnd, setRefusedEnd] = useState<string | null>(null);
  const future = !Number.isNaN(Date.parse(until)) && new Date(`${until}T23:59:59Z`) > new Date();
  const endError = !future
    ? 'A grant ends, and in the future.'
    : !withinTerm(until, limit)
      ? `These tools may be granted for ${limit} days at most — until ${dateIn(limit ?? 0)}. A superadmin lengthens a term on Connections → How long a grant lasts.`
      : refusedEnd;
  const endsOk = future && withinTerm(until, limit);
  // Fill for me (SG-05): a renewal, from the agent's latest grant. Its end is
  // never copied: when a grant ends is decided again, every time (§15).
  const [filling, setFilling] = useState(false);
  const earlier = useAssignments(agent.id);
  const latest = [...(earlier.data?.assignments ?? [])].sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0];
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="§15 · capability follows the work"
      title="Grant an assignment"
      secondary={{ label: step === 1 ? 'Cancel' : 'Back', onClick: () => (step === 1 ? onClose() : setStep(1)) }}
      primary={
        step === 1
          ? { label: 'Next', disabled: title.trim().length < 4 || !endsOk || (list(scopes).length === 0 && list(tools).length === 0), onClick: () => setStep(2) }
          : {
              label: 'Mint the token and grant it',
              disabled: token === '' || grant.isPending,
              onClick: () =>
                grant.mutate(
                  { agentId: agent.id, title: title.trim(), validTo: endsAt(until, limit).toISOString(), scopes: list(scopes), tools: list(tools), supervisionToken: token },
                  {
                    onSuccess: () => { toast({ text: `${agent.givenName} holds it until ${new Date(until).toLocaleDateString('en-GB')}. Then it lapses.` }); onClose(); },
                    // A refusal about the end is fixed where the end is chosen:
                    // back to the first step, everything kept. The term is
                    // checked before the token is spent, so the token stays good.
                    onError: (error) => {
                      const failure = failureOf(error);
                      if (failure.kind === 'refused' && (failure.code === 'beyond_term' || failure.code === 'end_required')) {
                        setRefusedEnd(failure.detail);
                        setStep(1);
                      }
                    },
                  },
                ),
            }
      }
    >
      {step === 1 ? (
        <>
          <p className="m-0 text-body">Capability ends with the work, so the work has to be named — and there is no open-ended option. §15 grants end.</p>
          {filling ? (
            <FillForMe
              title="Filled from an earlier grant"
              source={latest ? `From “${latest.title}”, granted ${new Date(latest.validFrom).toLocaleDateString('en-GB')}${latest.state === 'ended' ? ', now ended' : ' and still live'}. When this one ends is yours to decide again.` : `${agent.givenName} has held no grant yet, so there is nothing to fill from.`}
              lines={
                latest
                  ? [
                      { label: 'The work', value: latest.title },
                      { label: 'May do', value: latest.scopes.map(scopeWords).join(', ') || '—' },
                      { label: 'Tools', value: latest.tools.map(toolName).join(', ') || '—' },
                      { label: 'Ends', value: null },
                    ]
                  : []
              }
              onConfirm={
                latest
                  ? () => {
                      setTitle(latest.title);
                      setScopes(latest.scopes.join(', '));
                      setTools(latest.tools.join(', '));
                      setFilling(false);
                    }
                  : undefined
              }
              onDismiss={() => setFilling(false)}
            />
          ) : (
            <button type="button" className="min-h-tap self-start text-dense underline underline-offset-2" onClick={() => setFilling(true)}>
              Fill from an earlier grant
            </button>
          )}
          <Field label="The work">
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Autumn trade-fair follow-ups" aria-label="The work" />
          </Field>
          <Field label="Scopes" hint="Space or comma separated, e.g. outbound.threads. Default-deny: nothing not listed is reachable.">
            <TextInput value={scopes} onChange={(e) => setScopes(e.target.value)} aria-label="Scopes" />
          </Field>
          <Field label="Tools" hint="Tool ids from the library, e.g. comms.gmail_draft_create.">
            <TextInput value={tools} onChange={(e) => setTools(e.target.value)} aria-label="Tools" />
          </Field>
          {/* The quick choices sit outside the field: a label forwards its
              click to the first control inside it, which would be a choice. */}
          <div className="flex flex-wrap gap-2" role="group" aria-label="How long">
            {endChoices(limit).map((c) => (
              <Button
                key={c.days}
                type="button"
                variant="quiet"
                aria-pressed={until === dateIn(c.days)}
                onClick={() => { setUntil(dateIn(c.days)); setRefusedEnd(null); }}
              >
                {c.label}
              </Button>
            ))}
          </div>
          <Field
            label="Ends"
            hint={limit === null ? 'Every grant ends (§15).' : `These tools may be granted for up to ${limit} days. Every grant ends (§15).`}
            error={endError ?? undefined}
          >
            <input
              type="date"
              className="min-h-tap self-start rounded-brand border border-control bg-raised px-3 text-body"
              value={until}
              {...(limit !== null ? { max: dateIn(limit) } : {})}
              onChange={(e) => { setUntil(e.target.value); setRefusedEnd(null); }}
              aria-label="Ends"
            />
          </Field>
        </>
      ) : (
        <>
          <p className="m-0 text-body">Widening what an agent can reach is a critical operation (§17). The token is bound to this agent and spent once.</p>
          <TokenPanel operation="iam.grant_scope" target={`agent:${agent.handle}`} token={token} onToken={setToken} canMint />
          {grant.isError && <Refusal failure={failureOf(grant.error)} compact />}
        </>
      )}
    </Dialog>
  );
}

export function EndAssignment({ assignmentId, title, onClose }: { assignmentId: string; title: string; onClose: () => void }) {
  const end = useEndAssignment();
  const toast = useToast();
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="§15"
      title="End this assignment now?"
      danger
      note="Takes away, so no token"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'End it',
        disabled: end.isPending,
        onClick: () => end.mutate(assignmentId, { onSuccess: () => { toast({ text: `“${title}” ended. The capability went with it.`, tone: 'danger' }); onClose(); } }),
      }}
    >
      <p className="m-0 text-body">The capability stops resolving on the next call. Nothing the agent did under it is touched, and the record keeps who ended it and when.</p>
      {end.isError && <Refusal failure={failureOf(end.error)} compact />}
    </Dialog>
  );
}

export function AdmitTool({ tool, onClose }: { tool: WireTool; onClose: () => void }) {
  const admit = useAdmitTool();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Control room · superadmin decides"
      title="Admit this tool?"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and admit it',
        disabled: token === '' || note.trim().length < 8 || admit.isPending,
        onClick: () =>
          admit.mutate(
            { toolId: tool.id, note: note.trim(), supervisionToken: token },
            { onSuccess: () => { toast({ text: `${tool.id} is admitted. No department holds it until a grant says so.` }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">
        Admitting is the decision; signing stays in the vault and publishing refuses a manifest nobody admitted. Every tool sits behind the gateway, held to its signed manifest.
      </p>
      <Field label="Why" hint="Read by whoever asks later why this tool is in the library.">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
      </Field>
      <TokenPanel operation="tool.admit" target={`tool:${tool.id}`} token={token} onToken={setToken} canMint />
      {admit.isError && <Refusal failure={failureOf(admit.error)} compact />}
    </Dialog>
  );
}

export function WithdrawTool({ tool, onClose }: { tool: WireTool; onClose: () => void }) {
  const withdraw = useWithdrawTool();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [token, setToken] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Control room · superadmin decides"
      title="Withdraw this tool?"
      danger
      note={DANGER_NOTE}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and withdraw it',
        disabled: token === '' || note.trim().length < 8 || withdraw.isPending,
        onClick: () =>
          withdraw.mutate(
            { toolId: tool.id, note: note.trim(), supervisionToken: token },
            { onSuccess: () => { toast({ text: `${tool.id} is withdrawn. The gateway refuses it from now.`, tone: 'danger' }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">The edge stops honouring its manifest at once, ahead of the expiry. Every grant that names it stays where it is and resolves to nothing until it is admitted again.</p>
      <Field label="Why">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
      </Field>
      <TokenPanel operation="tool.withdraw" target={`tool:${tool.id}`} token={token} onToken={setToken} canMint />
      {withdraw.isError && <Refusal failure={failureOf(withdraw.error)} compact />}
    </Dialog>
  );
}

/**
 * A vendor's hosted assistant — ChatGPT — admitted as a client that signs in
 * as people (ADR-0142). Like admitting a vendor's tool: its transfer
 * paperwork first (the Workspace step), then a token bound to the vendor,
 * naming exactly the redirect addresses the vendor publishes.
 */
export function AdmitHostedClient({ vendor, name, onClose }: { vendor: string; name: string; onClose: () => void }) {
  const admit = useAdmitHostedClient();
  const toast = useToast();
  const [uris, setUris] = useState('');
  const [note, setNote] = useState('');
  const [token, setToken] = useState('');
  const redirectUris = uris.split(/\s+/).map((u) => u.trim()).filter((u) => u !== '');
  const wellFormed = redirectUris.length > 0 && redirectUris.length <= 5 && redirectUris.every((u) => /^https:\/\/[^\s#]{1,200}$/.test(u));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Control room · superadmin decides"
      title={`Admit ${name} as a hosted client?`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and admit it',
        disabled: token === '' || !wellFormed || note.trim().length < 8 || admit.isPending,
        onClick: () =>
          admit.mutate(
            { vendor, redirectUris, note: note.trim(), supervisionToken: token },
            { onSuccess: () => { toast({ text: `${name} is admitted. Each person still approves their own connection on the sign-in page.` }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">
        {name} runs outside the EU. Everything it reads as a person is sent to its vendor, so its DPA and transfer mechanism are marked on the Workspace first, and it registers only with exactly these addresses. What it reaches is the MCP door’s tool table: reads, and asks a person decides on; never a send, never a person’s own steps.
      </p>
      <Field label="Redirect addresses" hint="One per line, copied from the vendor’s documentation for MCP connectors. https only, at most five.">
        <Textarea value={uris} onChange={(e) => setUris(e.target.value)} aria-label="Redirect addresses" rows={3} />
      </Field>
      <Field label="Why" hint="Read by whoever asks later why this assistant may sign in as people.">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
      </Field>
      <TokenPanel operation="client.admit" target={`client:${vendor}`} token={token} onToken={setToken} canMint />
      {admit.isError && <Refusal failure={failureOf(admit.error)} compact />}
    </Dialog>
  );
}

export function WithdrawHostedClient({ client, onClose }: { client: WireHostedClient; onClose: () => void }) {
  const withdraw = useWithdrawHostedClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="ADR-0142"
      title={`Withdraw ${client.name}?`}
      danger
      note="Takes away, so no token"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Withdraw it',
        disabled: note.trim().length < 4 || withdraw.isPending,
        onClick: () =>
          withdraw.mutate(
            { vendor: client.vendor, note: note.trim() },
            { onSuccess: (r) => { toast({ text: `${client.name} withdrawn: ${r.grantsEnded} sign-in(s) and ${r.tokensEnded} token(s) ended.`, tone: 'danger' }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">
        Every person signed in through it is signed out at once: their refresh grants and access tokens end in the same transaction, and the edge refuses the vendor’s registrations on their next call. Admitting it again is a new admission, with a token.
      </p>
      <Field label="Why">
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
      </Field>
      {withdraw.isError && <Refusal failure={failureOf(withdraw.error)} compact />}
    </Dialog>
  );
}
